import { ApolloError } from "@apollo/client";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Collapse,
  Input,
  Text,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { DocumentType, serverGql } from "__generated__/serverGql";
import LoginLayoutButton from "components/Layout/LoginLayoutButton";
import ContactEmailLink from "components/Shared/ContactEmailLink";
import Form from "components/Shared/Forms/Form";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import LinkButton from "components/Shared/LinkButton";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import getMaybeList from "components/Shared/getMaybeList";
import TermsLink from "components/Shared/TermsLink";
import PasswordRequirements from "components/Core/Auth/PasswordRequirements";
import {
  getServerGraphQLErrorMatchersForFields,
  makeServerGraphQLErrorProcessor,
} from "utils/ServerGraphQLErrorExtensions";
import getApolloErrorMessages from "utils/apollo/getApolloErrorMessages";
import serverClient from "utils/apollo/serverClient";
import { assertPresence } from "utils/assertions";
import { LOGIN } from "utils/links";
import { email, password } from "utils/schemas/user";

const SIGNUP_USER_MUTATION = serverGql(/* ServerGraphQL */ `
  mutation SignupUser($email: String!, $password: String!) {
    signupUser(email: $email, password: $password) {
      auth {
        ...AuthPayloadFields
      }
    }
  }
`);

export type SignupUserData = DocumentType<typeof SIGNUP_USER_MUTATION>;

type AccountFormFields = {
  email: string;
  password: string;
};

type Props = {
  onCompleted: (data: SignupUserData) => Promise<void>;
};

const accountSchema: yup.ObjectSchema<AccountFormFields> = yup.object().shape({
  email,
  password,
});

const { FormField } = createFormFieldComponents<AccountFormFields>({
  schema: accountSchema,
});

const processAccountApolloError = makeServerGraphQLErrorProcessor(
  getServerGraphQLErrorMatchersForFields(["email", "password"]),
);

export default function AccountSignupForm({ onCompleted }: Props) {
  const [accountError, setAccountError] = useState<ApolloError>();
  const formContext = useForm<AccountFormFields>({
    mode: "onTouched",
    resolver: yupResolver(accountSchema),
  });

  const [handleSubmit, { isLoading }] = useCreateValidSubmitHandler(
    async (formData: AccountFormFields) => {
      setAccountError(undefined);
      const result = await serverClient.mutate({
        mutation: SIGNUP_USER_MUTATION,
        variables: formData,
      });

      assertPresence(
        result.data,
        "User signup completed without returning auth data.",
      );

      await onCompleted(result.data);
      return result;
    },
    {
      isSuccessToasted: false,
      onNonFieldError: (error) => setAccountError(error),
      processApolloError: processAccountApolloError,
    },
  );

  return (
    <Form formContext={formContext} onValidSubmit={handleSubmit}>
      <Collapse in={!!accountError}>
        <Alert status="error" variant="left-accent" mb={10}>
          <AlertIcon />
          <AlertDescription>
            {accountError && getMaybeList(getApolloErrorMessages(accountError))}
          </AlertDescription>
        </Alert>
      </Collapse>

      <FormField
        name="email"
        label={null}
        placeholder="Email"
        autoComplete="username"
        autoFocus
        mt={6}
        renderInput={(props) => (
          <Input {...props} type="email" autoFocus size="lg" />
        )}
      />

      <FormField
        name="password"
        label={null}
        placeholder="Password"
        autoComplete="new-password"
        mt={6}
        mb={0}
        renderInput={(props) => <Input {...props} type="password" size="lg" />}
      />

      <PasswordRequirements />

      <LoginLayoutButton type="submit" mt={6} isLoading={isLoading}>
        Create Account
      </LoginLayoutButton>

      <Box textAlign="center">
        <Text fontSize="xs" color="gray.500" mt={4}>
          By using this service you are agreeing to the <TermsLink />
        </Text>

        <Box
          display="flex"
          alignItems="baseline"
          justifyContent="center"
          gap={2}
          mt={8}
          fontSize="sm"
          fontWeight={300}
          color="gray.600"
          flexWrap="wrap"
        >
          Already have an account?{" "}
          <LinkButton
            href={LOGIN}
            variant="outline"
            colorScheme="lightPinkButton"
            size="xs"
            rightIcon={<ArrowForwardIcon />}
          >
            Login
          </LinkButton>
        </Box>

        <Box fontSize="sm" color="gray.500" mt={8}>
          Need help? Please contact <ContactEmailLink />.
        </Box>
      </Box>
    </Form>
  );
}
